// @flow
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';
import {useCommonStyles} from '../../common/components/CommonStyle';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import Tag from '../../../../elements/Tag/Tag';

import environment from '../../../../environment';

import {useForm, useWatch} from 'react-hook-form';

import type {Domain} from '../../../../entities';
import {TIENDADA_LINKS} from '../../../../constants';

type Props = {|
  open: boolean,
  handleClose: (success?: boolean) => void,
  isValidatingSsl?: boolean,
  sslIsValid?: boolean,
  isGeneratingSsl: boolean,
  startGeneratingSsl?: boolean,
  errorValidatingSsl?: string,
  errorGeneratingSsl?: string,
  validateSsl: ({domain: string}) => void,
  generateSsl: ({domain: string}) => void,
  waitToGenerateSsl?: boolean,
  customDomain?: Domain,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 600,
    padding: theme.spacing(4, 0),
    overflow: 'hidden',
  },
  productContainer: {
    height: '70%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
    width: '100%',
  },
  domainRow: {
    position: 'relative',
    marginBottom: theme.spacing(4),
    display: 'flex',
    width: '100%',
  },
  text: {
    color: colors.grey700,
    fontSize: 12,
    width: '100%',
    textAlign: 'start',
  },
  link: {
    color: colors.purple,
    fontWeight: 700,
    textDecoration: 'none',
  },
  subItem: {
    display: 'block',
    marginLeft: 8,
  },
  rootDomain: {
    marginTop: 16,
    marginLeft: theme.spacing(1),
  },
  helperTextValid: {
    color: colors.success,
  },
  saveButton: {
    marginTop: '16px',
  },
  textError: {
    fontSize: 12,
    color: colors.error,
  },
  domainTag: {
    position: 'absolute',
    height: '65%',
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
    top: 0,
    right: 0,
  },
}));

export default function CustomDomainModal(props: Props) {
  const {
    open,
    handleClose,
    isValidatingSsl,
    sslIsValid,
    isGeneratingSsl,
    validateSsl,
    errorValidatingSsl,
    errorGeneratingSsl,
    generateSsl,
    startGeneratingSsl,
    waitToGenerateSsl,
    customDomain,
  } = props;

  const {register, errors, handleSubmit, control, trigger} = useForm({
    mode: 'onChange',
  });

  const [formError, setFormError] = useState(false);
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const domain = useWatch({
    control,
    name: 'domain',
  });

  useEffect(() => {
    trigger();
  }, [trigger, errorValidatingSsl, sslIsValid, formError]);

  useEffect(() => {
    if (sslIsValid && isGeneratingSsl && !waitToGenerateSsl) {
      handleClose(true);
    }
  }, [isGeneratingSsl, sslIsValid, handleClose, waitToGenerateSsl]);

  const handleSubmitSubdomain = data => {
    if (
      !sslIsValid &&
      domain &&
      (!errors.domain || errors.domain.type === 'valid')
    ) {
      validateSsl({domain});
    }
  };

  const handleCloseModal = () => {
    handleClose(false);
  };

  useEffect(() => {
    if (sslIsValid === false) {
      setFormError(true);
    }
  }, [sslIsValid]);

  useEffect(() => {
    setFormError(false);
  }, [domain]);

  useEffect(() => {
    if (domain && sslIsValid) {
      generateSsl({domain});
    }
  }, [domain, sslIsValid, generateSsl]);

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography className={classes.title} align="left">
          Usa tu propio dominio
        </Typography>
        <div className={classes.text} style={{marginTop: 8}}>
          Sigue estos pasos: <br />
          <ol>
            <li>Ingresa al panel de tu administrador de dominio.</li>
            <li>Clic en la configuración de tu dominio. </li>
            <li>Clic en configurar DNS.</li>
            <li>
              <span>Crea un record A</span>
              <span className={classes.subItem}>Nombre: @</span>
              <span className={classes.subItem}>
                Valor: {environment.dnsRecordValue}
              </span>
            </li>
            <li>
              <span>Crea un record CNAME</span>
              <span className={classes.subItem}>Nombre: www</span>
              <span className={classes.subItem}>
                Valor: tienda.com (colocar tu dominio)
              </span>
            </li>
            <li>
              <span>Coloca aquí tu dominio</span>
            </li>
          </ol>
        </div>

        <div className={classes.domainRow}>
          <CustomTextField
            disabled={waitToGenerateSsl}
            fullWidth
            defaultValue={customDomain && customDomain.domainName}
            name="domain"
            error={!!errors.domain}
            FormHelperTextProps={{
              className:
                !errors.domain && domain && domain.length > 0
                  ? classes.helperTextValid
                  : '',
            }}
            helperText={
              errors.domain ? errors.domain.message : 'Ejemplo: tienda.com'
            }
            inputRef={register({
              required: 'Campo obligatorio',
              pattern: {
                value: /^(?:www\.)?[a-zA-Z0-9-]+?(?:\.[a-zA-Z]{2,}){1,3}$/,
                message:
                  'El dominio no puede contener mayúsculas ni caracteres especiales como “!%$&”\n Ejemplo: tienda.com',
              },
              validate: {
                valid: v => {
                  return (
                    !formError ||
                    (sslIsValid && domain.length > 0) ||
                    errorValidatingSsl ||
                    'Este dominio ya se encuentra en uso.'
                  );
                },
              },
            })}
            type="text"
          />
          {waitToGenerateSsl && (
            <div className={classes.domainTag}>
              <Tag label="En proceso" type="warning" />
            </div>
          )}
        </div>

        <Typography className={classes.text} paragraph>
          Puede demorar entre 2h a 24h la configuración de tu dominio. recuerda
          que puedes ir utilizando tu sibdominio proporcionado por tiendada.
        </Typography>
        <Typography className={classes.text} paragraph>
          Si tienes problemas, por favor contáctate con nuestro{' '}
          <a
            href={TIENDADA_LINKS.customerSupport.general}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            equipo de soporte.
          </a>
        </Typography>
        <Typography className={classes.textError}>
          {errorGeneratingSsl &&
            `${errorGeneratingSsl} cierra el modal e intenta nuevamente.`}
        </Typography>
        <CustomButton
          type="button"
          variant="contained"
          color="primary"
          size="large"
          className={`${commonClasses.saveButton} ${classes.saveButton}`}
          disabled={
            waitToGenerateSsl ||
            formError ||
            errors.domain ||
            isValidatingSsl ||
            startGeneratingSsl
          }
          onClick={handleSubmit(handleSubmitSubdomain)}
        >
          Guardar
        </CustomButton>
      </div>
    </CustomModal>
  );
}

CustomDomainModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  domain: PropTypes.string,
  saveGeneralSettings: PropTypes.func,
  validationResult: PropTypes.bool,
  isValidating: PropTypes.bool,
  domainChangeToken: PropTypes.string,
  isValidatingSsl: PropTypes.bool,
  sslIsValid: PropTypes.bool,
  isGeneratingSsl: PropTypes.bool,
  errorGeneratingSsl: PropTypes.string,
  validateSsl: PropTypes.func.isRequired,
  generateSsl: PropTypes.func.isRequired,
};
